import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import styled from 'styled-components'
import { IConnection } from '../../../../../interfaces/models/connection'
import ConnectionSidebarFragment from '../../../../../fragments/ConnectionSidebarFragment'
import { useInboxStore } from '../../../../../stores/inbox/useInboxStore'
import { useInboxHandlers } from '../../../../../services/actions/inbox/useInboxHandlers'
import InboxNonConnectionSidebar from './InboxNonConnectionSidebar'
import { useMinSkeletonDuration } from '../skeleton/useMinSkeletonDuration'
import { inboxSkeletonSurface } from '../skeleton/inboxSkeletonStyles'

const leanObjectIdString = (ref: unknown): string => {
    if (ref == null) return ''
    if (typeof ref === 'string') return ref
    if (typeof ref === 'object' && ref !== null && '$oid' in ref && typeof (ref as { $oid: unknown }).$oid === 'string') {
        return (ref as { $oid: string }).$oid
    }
    if (typeof ref === 'object' && ref !== null && '_id' in ref) {
        return leanObjectIdString((ref as { _id: unknown })._id)
    }
    return String(ref)
}

const ConversationDetails: React.FC = () => {
    const { store: { selectedConversation, selectedConnection, fetchingConversations } } = useInboxStore()
    const showConversationListSkeleton = useMinSkeletonDuration(fetchingConversations)
    const {
        getConnectionHandler,
        applyTagsToConversationsHandler,
        removeTagsFromConversationsHandler,
        addInboxParticipantToLeadDeltaHandler,
        addInboxParticipantAndEnrichHandler,
        setInboxSelectedConnectionHandler,
    } = useInboxHandlers()

    const [connectionLookupDone, setConnectionLookupDone] = useState(false)
    const [addingToLeadDelta, setAddingToLeadDelta] = useState(false)
    const [addAndEnrichInProgress, setAddAndEnrichInProgress] = useState(false)

    const participant = selectedConversation?.participants?.[0]
    const isSingleParticipant = selectedConversation?.participants?.length === 1
    const conversationId = selectedConversation?.conversationId

    /** Reset lookup flag before paint when the thread changes; avoids one frame with stale lookupDone + null connection (non-LD panel flash). */
    useLayoutEffect(() => {
        if (!isSingleParticipant || !selectedConversation?.participants || selectedConversation.participants.length !== 1) {
            return
        }
        setConnectionLookupDone(false)
    }, [
        isSingleParticipant,
        conversationId,
        selectedConversation?._id,
        participant?.publicIdentifier,
        participant?.memberId,
        leanObjectIdString(participant?._id),
        leanObjectIdString(selectedConversation?.connections?.[0]),
    ])

    useEffect(() => {
        if (!selectedConversation?.participants || !isSingleParticipant) {
            setConnectionLookupDone(true)
            return
        }
        let alive = true
        setConnectionLookupDone(false)
        void getConnectionHandler(selectedConversation).finally(() => {
            if (alive) setConnectionLookupDone(true)
        })
        return () => {
            alive = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        conversationId,
        selectedConversation?._id,
        participant?.publicIdentifier,
        participant?.memberId,
        leanObjectIdString(participant?._id),
        leanObjectIdString(selectedConversation?.connections?.[0]),
        isSingleParticipant,
    ])

    const handleConversationApplyRemoveTags = useCallback(async (connectionId: string, tagsToAdd: string[], tagsToRemove: string[]) => {
        if (tagsToAdd.length > 0) {
            await applyTagsToConversationsHandler(tagsToAdd, [connectionId], false)
        }
        if (tagsToRemove.length > 0) {
            await removeTagsFromConversationsHandler(tagsToRemove, [connectionId], false)
        }
    }, [applyTagsToConversationsHandler, removeTagsFromConversationsHandler])

    const handleAddToLeadDelta = useCallback(async () => {
        if (addingToLeadDelta || addAndEnrichInProgress) return
        setAddingToLeadDelta(true)
        try {
            await addInboxParticipantToLeadDeltaHandler()
        } finally {
            setAddingToLeadDelta(false)
        }
    }, [addInboxParticipantToLeadDeltaHandler, addingToLeadDelta, addAndEnrichInProgress])

    const handleAddToLeadDeltaAndEnrich = useCallback(
        async (flags: { privateEmailChecked: boolean; businessEmailChecked: boolean; phoneChecked: boolean }) => {
            if (addAndEnrichInProgress || addingToLeadDelta) return
            setAddAndEnrichInProgress(true)
            try {
                await addInboxParticipantAndEnrichHandler(flags)
            } finally {
                setAddAndEnrichInProgress(false)
            }
        },
        [addInboxParticipantAndEnrichHandler, addAndEnrichInProgress, addingToLeadDelta],
    )

    if (showConversationListSkeleton) {
        return (
            <Container>
                <ScrollableContent>
                    <SidebarSkeleton>
                        <SkeletonLine $w="60px" $h="60px" $circle />
                        <SkeletonLine $w="120px" $h="14px" />
                        <SkeletonLine $w="90px" $h="11px" />
                        <SkeletonLine $w="100%" $h="32px" $radius="16px" style={{ marginTop: 8 }} />
                        <SkeletonLine $w="100%" $h="80px" $radius="6px" style={{ marginTop: 12 }} />
                        <SkeletonLine $w="100%" $h="60px" $radius="6px" />
                    </SidebarSkeleton>
                </ScrollableContent>
            </Container>
        )
    }

    if (!selectedConversation) return null

    const showSidebarSkeleton = isSingleParticipant && !connectionLookupDone
    const showNonConnectionPanel =
        connectionLookupDone && isSingleParticipant && !selectedConnection && !!participant

    return (
        <Container>
            <ScrollableContent>
                {showSidebarSkeleton ? (
                    <SidebarSkeleton>
                        <SkeletonLine $w="60px" $h="60px" $circle />
                        <SkeletonLine $w="120px" $h="14px" />
                        <SkeletonLine $w="90px" $h="11px" />
                        <SkeletonLine $w="100%" $h="32px" $radius="16px" style={{ marginTop: 8 }} />
                        <SkeletonLine $w="100%" $h="80px" $radius="6px" style={{ marginTop: 12 }} />
                        <SkeletonLine $w="100%" $h="60px" $radius="6px" />
                    </SidebarSkeleton>
                ) : selectedConnection ? (
                    <ConnectionSidebarFragment
                        inboxSurface
                        connection={selectedConnection as IConnection}
                        conversationTags={selectedConversation?.tags || []}
                        onConversationApplyRemoveTags={handleConversationApplyRemoveTags}
                        onInboxConnectionResolvedForEnrich={setInboxSelectedConnectionHandler}
                    />
                ) : showNonConnectionPanel ? (
                    <InboxNonConnectionSidebar
                        participant={participant}
                        onAddToLeadDelta={handleAddToLeadDelta}
                        addingToLeadDelta={addingToLeadDelta}
                        onAddToLeadDeltaAndEnrich={handleAddToLeadDeltaAndEnrich}
                        addAndEnrichInProgress={addAndEnrichInProgress}
                    />
                ) : (
                    <CompactPlaceholder>
                        <SkeletonLine $w="60px" $h="60px" $circle />
                        <SkeletonLine $w="120px" $h="14px" />
                        <SkeletonLine $w="90px" $h="11px" />
                    </CompactPlaceholder>
                )}
            </ScrollableContent>
        </Container>
    )
}

const Container = styled.div`
    width: clamp(280px, 27vw, 360px);
    flex-shrink: 0;
    align-self: stretch;
    min-height: 0;
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 100%;
    border-left: 1px solid ${props => props.theme.quinaryColor || '#dae0e8'};
    overflow: hidden;
    background: #f6f8fb;
`

const ScrollableContent = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 16px;
    scrollbar-width: thin;
    scrollbar-color: #e8eaed transparent;
    &::-webkit-scrollbar { width: 4px; background: transparent; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: #e8eaed; border-radius: 2px; }
`

const SidebarSkeleton = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    gap: 10px;
    box-sizing: border-box;
`

const CompactPlaceholder = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
`

const SkeletonLine = styled.div<{ $w: string; $h: string; $circle?: boolean; $radius?: string }>`
    width: ${p => p.$w};
    max-width: 100%;
    height: ${p => p.$h};
    flex-shrink: 0;
    border-radius: ${p => (p.$circle ? '50%' : p.$radius || '4px')};
    ${inboxSkeletonSurface}
`

export default ConversationDetails
